<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { showConfirm } from '@/utils'
import { useSessionStore } from '@/stores/session'

const refreshIcon = new URL('../assets/imgs/refresh-icon.png', import.meta.url).href
const closeIcon = new URL('../assets/imgs/close-icon.png', import.meta.url).href

const EDGE_MARGIN = 6
const DRAG_THRESHOLD = 5

const authStore = useAuthStore()
const router = useRouter()
const sessionStore = useSessionStore()

const barRef = ref<HTMLElement | null>(null)
const posX = ref(0)
const posY = ref(0)
const isPositionReady = ref(false)
const isDragging = ref(false)

let isPointerActive = false
let suppressClick = false
let dragStartX = 0
let dragStartY = 0
let dragOriginX = 0
let dragOriginY = 0

const barStyle = computed(() => {
  if (!isPositionReady.value) {
    return {
      top: 'calc(env(safe-area-inset-top, 0px) + 60px)',
      right: 'calc(env(safe-area-inset-right, 0px) + 6px)',
    }
  }

  return {
    top: `${posY.value}px`,
    left: `${posX.value}px`,
  }
})

function getBounds() {
  const el = barRef.value
  const width = el?.offsetWidth ?? 0
  const height = el?.offsetHeight ?? 0

  return {
    minX: EDGE_MARGIN,
    minY: EDGE_MARGIN,
    maxX: Math.max(EDGE_MARGIN, window.innerWidth - width - EDGE_MARGIN),
    maxY: Math.max(EDGE_MARGIN, window.innerHeight - height - EDGE_MARGIN),
  }
}

function clampPosition(x: number, y: number) {
  const { minX, minY, maxX, maxY } = getBounds()
  return {
    x: Math.min(Math.max(x, minX), maxX),
    y: Math.min(Math.max(y, minY), maxY),
  }
}

function snapToEdge() {
  const el = barRef.value
  if (!el) return

  const width = el.offsetWidth
  const centerX = posX.value + width / 2
  const snapX =
    centerX < window.innerWidth / 2 ? EDGE_MARGIN : window.innerWidth - width - EDGE_MARGIN

  const clamped = clampPosition(snapX, posY.value)
  posX.value = clamped.x
  posY.value = clamped.y
}

function initPosition() {
  nextTick(() => {
    const el = barRef.value
    if (!el) return

    const rect = el.getBoundingClientRect()
    posX.value = rect.left
    posY.value = rect.top
    isPositionReady.value = true
  })
}

function removePointerListeners() {
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', onPointerUp)
  window.removeEventListener('pointercancel', onPointerUp)
}

function onPointerDown(e: PointerEvent) {
  if (e.button !== 0 || !isPositionReady.value) return

  isPointerActive = true
  isDragging.value = false
  dragStartX = e.clientX
  dragStartY = e.clientY
  dragOriginX = posX.value
  dragOriginY = posY.value

  window.addEventListener('pointermove', onPointerMove)
  window.addEventListener('pointerup', onPointerUp)
  window.addEventListener('pointercancel', onPointerUp)
}

function onPointerMove(e: PointerEvent) {
  if (!isPointerActive) return

  const dx = e.clientX - dragStartX
  const dy = e.clientY - dragStartY

  if (!isDragging.value) {
    if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return
    isDragging.value = true
  }

  const clamped = clampPosition(dragOriginX + dx, dragOriginY + dy)
  posX.value = clamped.x
  posY.value = clamped.y
}

function onPointerUp() {
  if (!isPointerActive) return

  const wasDragging = isDragging.value
  isPointerActive = false
  isDragging.value = false
  removePointerListeners()

  if (wasDragging) {
    snapToEdge()
    suppressClick = true
    requestAnimationFrame(() => {
      suppressClick = false
    })
  }
}

function onWindowResize() {
  if (!isPositionReady.value) return
  snapToEdge()
}

function refreshPage() {
  if (suppressClick) return
  window.location.reload()
}

async function onCloseClick() {
  if (suppressClick) return

  try {
    await showConfirm('温馨提示', '您确定退出当前页面吗？', {
      closeOnClickOverlay: true,
    })
    await router.replace({ path: '/' })

    if (window.postMessage) {
      window.postMessage('close_lucky_window', '*')
    }
  } catch {
    // 取消或关闭遮罩
  }
}

onMounted(() => {
  initPosition()
  window.addEventListener('resize', onWindowResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', onWindowResize)
  removePointerListeners()
})
</script>

<template>
  <div
    v-if="sessionStore.sessionId && authStore.isMerMember"
    ref="barRef"
    class="mer-member-floating-bar pointer-events-auto fixed z-1000 flex items-center gap-2 px-2 py-1 overflow-hidden rounded-full touch-none select-none cursor-grab active:cursor-grabbing"
    :class="{ 'is-dragging': isDragging }"
    :style="barStyle"
    role="toolbar"
    aria-label="商户快捷操作"
    @pointerdown="onPointerDown"
  >
    <button
      type="button"
      class="flex flex-1 items-center justify-center active:bg-white/10"
      aria-label="刷新页面"
      @click="refreshPage"
    >
      <img :src="refreshIcon" alt="" class="size-5 object-contain pointer-events-none" />
    </button>
    <span class="w-px h-4 shrink-0 bg-white/70" aria-hidden="true" />
    <button
      type="button"
      class="flex flex-1 items-center justify-center active:bg-white/10"
      aria-label="关闭并返回首页"
      @click="onCloseClick"
    >
      <img :src="closeIcon" alt="" class="size-5 object-contain pointer-events-none" />
    </button>
  </div>
</template>

<style scoped>
.mer-member-floating-bar {
  background-color: rgba(0, 0, 0, 0.5);
  transition:
    left 0.2s ease-out,
    top 0.2s ease-out;
}

.mer-member-floating-bar.is-dragging {
  transition: none;
}
</style>
